"use client";

import { cn } from "@/lib/utils";
import type { Participant } from "../base-match-template";
import {
  ContactShadow,
  FieldMatchFrame,
  type FieldMatchState,
  FieldScore,
  FieldScoreDetail,
  FloatingProp,
  Floodlight,
  PerspectivePlane,
  type PlaneSpec,
  projectOnPlane,
  StadiumStands,
} from "./field-sport-scene-kit";

export type FootballPitchPreviewProps = {
  yourTeam: Participant[];
  opponentTeam: Participant[];
  yourWon?: boolean;
  matchScore?: { yourGoals: number; opponentGoals: number };
  periods?: Array<{ label: string; yourScore: number; opponentScore: number }>;
  penalties?: { yourScore: number; opponentScore: number } | null;
  linkToProfiles?: boolean;
};

/** Telstar-style ball: white leather, black pentagons, a specular gloss. */
const Football = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "relative overflow-hidden rounded-full bg-[radial-gradient(circle_at_35%_30%,#ffffff_0%,#eef0ee_40%,#b9beba_100%)]",
      className,
    )}
    style={{
      boxShadow:
        "inset -2px -3px 5px rgba(0,0,0,0.45), inset 1px 2px 3px rgba(255,255,255,0.9)",
    }}
  >
    <div
      className="absolute top-[33%] left-[31%] h-[34%] w-[36%] bg-[#161616]"
      style={{
        clipPath: "polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)",
      }}
    />
    <div
      className="absolute top-[-12%] right-[-8%] h-[30%] w-[34%] rotate-45 bg-[#161616]/90"
      style={{
        clipPath: "polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)",
      }}
    />
    <div
      className="absolute bottom-[-10%] left-[-10%] h-[30%] w-[34%] -rotate-[135deg] bg-[#161616]/90"
      style={{
        clipPath: "polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)",
      }}
    />
    <div className="absolute top-[10%] left-[22%] h-[32%] w-[36%] rounded-full bg-white/80 blur-[1px]" />
  </div>
);

const LINE = "border-white/85";

const PITCH: PlaneSpec = {
  top: 68,
  depth: 176,
  tilt: 60,
  perspective: 300,
  farWidth: 112,
};
/** Centre spot: the pitch markings put it 75px into the plane. */
const CENTRE_SPOT = projectOnPlane(PITCH, 0.5, 75);

/** The pitch, drawn flat in plane coordinates and tilted by PerspectivePlane. */
function PitchMarkings() {
  return (
    <>
      {/* Mowing: 12 bands across the length, a faint cross-cut over them. */}
      <div className="absolute inset-0 bg-[repeating-linear-gradient(90deg,#2f8f40_0%,#2f8f40_8.333%,#267a35_8.333%,#267a35_16.666%)]" />
      <div className="absolute inset-0 bg-[repeating-linear-gradient(180deg,rgba(255,255,255,0.045)_0px,rgba(255,255,255,0.045)_15px,transparent_15px,transparent_30px)]" />
      {/* Turf grain */}
      <div className="absolute inset-0 [background-image:radial-gradient(rgba(0,0,0,0.16)_0.6px,transparent_0.9px),radial-gradient(rgba(190,255,170,0.12)_0.6px,transparent_0.9px)] [background-size:3px_3px,3px_3px] [background-position:0_0,1.5px_1.5px] opacity-60" />

      {/* Touchlines + goal lines */}
      <div
        className={cn(
          "absolute top-[8px] right-[5%] bottom-[30px] left-[5%] border-2",
          LINE,
        )}
      />
      {/* Halfway line, centre circle and spot */}
      <div className="absolute top-[8px] bottom-[30px] left-1/2 w-[2px] -translate-x-1/2 bg-white/85" />
      <div
        className={cn(
          "absolute top-[75px] left-1/2 h-[64px] w-[64px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2",
          LINE,
        )}
      />
      <div className="absolute top-[75px] left-1/2 h-[5px] w-[5px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/90" />

      {/* Penalty areas (the goal mouths fall just outside the frame) */}
      {(["left", "right"] as const).map((side) => (
        <div
          key={side}
          className={cn(
            "absolute top-[33px] bottom-[55px] w-[15%]",
            side === "left" ? "left-[5%]" : "right-[5%]",
          )}
        >
          <div
            className={cn(
              "absolute inset-0 border-2",
              LINE,
              side === "left" ? "border-l-0" : "border-r-0",
            )}
          />
        </div>
      ))}

      {/* Corner arcs */}
      <div className="absolute top-[8px] left-[5%] h-[10px] w-[10px] rounded-br-full border-r-2 border-b-2 border-white/85" />
      <div className="absolute top-[8px] right-[5%] h-[10px] w-[10px] rounded-bl-full border-b-2 border-l-2 border-white/85" />
    </>
  );
}

function FootballStadium() {
  return (
    <>
      {/* Night sky behind the roof line */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#03070b_0%,#071510_45%,#0a2414_100%)]" />

      <StadiumStands
        className="h-[70px]"
        crowdA="236,255,240"
        crowdB="40,200,110"
        baseClassName="bg-[linear-gradient(180deg,#04070a_0%,#0d1a17_100%)]"
        boardClassName="bg-[linear-gradient(90deg,#063d22_0%,#18c06a_18%,#e9fff2_34%,#18c06a_50%,#063d22_66%,#18c06a_82%,#e9fff2_100%)] opacity-80 shadow-[0_0_14px_rgba(40,230,130,0.55)]"
      />

      {/* The pitch, in broadcast perspective */}
      <PerspectivePlane plane={PITCH}>
        <PitchMarkings />
      </PerspectivePlane>

      {/* Floodlit haze where the stands meet the pitch */}
      <div className="absolute inset-x-0 top-[58px] h-[34px] bg-[linear-gradient(180deg,transparent,rgba(214,255,226,0.14)_45%,transparent)]" />

      {/* Floodlights: towers in the corners, beams pooling on the turf */}
      <Floodlight side="left" x="5%" y={9} beamTo="32%" />
      <Floodlight side="right" x="5%" y={9} beamTo="32%" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_42%_32%_at_30%_62%,rgba(236,255,236,0.14),transparent),radial-gradient(ellipse_42%_32%_at_70%_62%,rgba(236,255,236,0.14),transparent),radial-gradient(ellipse_30%_26%_at_50%_54%,rgba(255,255,236,0.12),transparent)]" />

      {/* Depth: the near touchline falls into shadow, edges vignette */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_62%,rgba(0,12,4,0.42)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_95%_at_50%_45%,transparent_55%,rgba(0,0,0,0.5)_100%)]" />
    </>
  );
}

export function FootballPitchPreview({
  yourTeam,
  opponentTeam,
  yourWon,
  matchScore,
  periods,
  penalties,
  linkToProfiles = false,
}: FootballPitchPreviewProps) {
  const opponentWon = opponentTeam.length > 0 && yourWon === false;
  const yourIsWinner = !!yourWon && !opponentWon;

  const matchState: FieldMatchState =
    yourTeam.length === 0 && opponentTeam.length === 0
      ? "EMPTY"
      : matchScore &&
          (matchScore.yourGoals > 0 ||
            matchScore.opponentGoals > 0 ||
            (periods && periods.length > 0) ||
            !!penalties)
        ? "FULL"
        : "NO_SCORE";

  const totalGoals = matchScore
    ? matchScore.yourGoals + matchScore.opponentGoals
    : 0;
  const shownPeriods = (periods ?? []).slice(-4);

  return (
    <FieldMatchFrame
      label="Football"
      frameClassName="bg-[#04100a]"
      matchState={matchState}
      scene={<FootballStadium />}
      yourTeam={yourTeam}
      opponentTeam={opponentTeam}
      yourWinner={yourIsWinner}
      opponentWinner={opponentWon}
      linkToProfiles={linkToProfiles}
      bandClassName="top-[50px] bottom-1"
      decor={
        matchState !== "NO_SCORE" ? null : (
          // Kickoff: the ball waits on the centre spot.
          <div
            className="absolute -translate-x-1/2 -translate-y-full"
            style={{ left: CENTRE_SPOT.left, top: CENTRE_SPOT.top + 2 }}
          >
            <ContactShadow className="-bottom-[3px] left-1/2 h-[7px] w-[22px] -translate-x-1/2" />
            <FloatingProp amplitude={2.5}>
              <Football className="h-[18px] w-[18px] @sm:h-[20px] @sm:w-[20px]" />
            </FloatingProp>
          </div>
        )
      }
      center={
        matchState === "FULL" && matchScore ? (
          <>
            <FieldScore
              transitionKey={totalGoals}
              left={matchScore.yourGoals}
              right={matchScore.opponentGoals}
              leftWinner={yourIsWinner}
              rightWinner={opponentWon}
            />
            {shownPeriods.length > 0 || penalties ? (
              <FieldScoreDetail>
                {shownPeriods.map((period, i) => (
                  <span
                    key={`${period.label}-${period.yourScore}-${period.opponentScore}`}
                    className="flex items-center gap-1.5 whitespace-nowrap"
                  >
                    {i > 0 ? <span className="text-white/35">·</span> : null}
                    <span className="text-white/50">{period.label}</span>
                    <span className="text-white/90">
                      {period.yourScore}-{period.opponentScore}
                    </span>
                  </span>
                ))}
                {penalties ? (
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    {shownPeriods.length > 0 ? (
                      <span className="text-white/35">·</span>
                    ) : null}
                    <span className="text-[#ffc65c]">
                      Pens {penalties.yourScore}-{penalties.opponentScore}
                    </span>
                  </span>
                ) : null}
              </FieldScoreDetail>
            ) : null}
          </>
        ) : null
      }
    />
  );
}
